import { z } from 'zod'
import { positiveNumber } from '../../../utils'
import { packagingPhysicalPropertiesSchema } from './packagingPhysicalPropertiesSchema'
import { productPhysicalPropertiesSchema } from './productPhysicalPropertiesSchema'
import { createNumericField } from './schemaUtils'

export const packagingItemSchema = z
  .object({
    id: z.number().optional(),
    packagingTemplateId: z.preprocess(
      (val) => (val === null ? undefined : val),
      z.number().optional()
    ),
    isHazardous: z.boolean().optional(),
    physicalPropertiesId: z.number().optional(),
    shipmentBookingId: z.number().optional(),
    сonsignmentId: z.number().optional(),
    packagingDetailId: z.number().optional(),
    numberOfPackages: z
      .number({ message: 'Number of packages is required' })
      .int()
      .max(32767, { message: 'Max 32,767' })
      .optional(),
    useDeclaredValue: z.boolean().optional(),
    declaredContentDescription: z
      .string()
      .max(200, { message: 'Max 200 characters' })
      .nullable()
      .optional(),
    amountInsured: z.number().optional(),
    insuranceRequired: z.boolean().optional(),
    temperatureControlled: z.boolean().optional(),
    temperatureSettingId: z.number().optional(),
    temperatureRange: z.number().optional(),
    setPointUnitId: z.number().optional(),
    setPoint: createNumericField(
      z.number().max(9999.9999, { message: 'Max 9,999.9999' }).optional()
    ),
    lowerWarning: createNumericField(
      z.number().max(9999.9999, { message: 'Max 9,999.9999' }).optional()
    ),
    upperWarning: createNumericField(
      z.number().max(9999.9999, { message: 'Max 9,999.9999' }).optional()
    ),
    lowerCritical: createNumericField(
      z.number().max(9999.9999, { message: 'Max 9,999.9999' }).optional()
    ),
    upperCritical: createNumericField(
      z.number().max(9999.9999, { message: 'Max 9,999.9999' }).optional()
    ),
    isStackable: z.boolean().optional(),
    declaredValue: z.preprocess(
      (val) => (val === '' ? null : val),
      z
        .number()
        .max(Number.MAX_SAFE_INTEGER, { message: 'Max 9,999,999,999,999.9900' })
        .nullable()
        .optional()
    ),
    serviceId: z.string().nullable().optional(),
    physicalProperties: packagingPhysicalPropertiesSchema,
    products: z
      .array(
        z
          .object({
            id: z.number().optional(),
            productDescription: z
              .string()
              .max(200, { message: 'Max 200 characters' })
              .optional()
              .nullable(),
            productId: z.number().nullable().optional(),
            productRevision: z.number().nullable().optional(),
            countryOfOriginId: z.number().nullable().optional(),
            quantity: z
              .number({ message: 'Quantity is required' })
              .int()
              .max(2147483647, { message: 'Max 2,147,483,647' }),
            unitPrice: z
              .number()
              .max(Number.MAX_SAFE_INTEGER, { message: 'Max 9,999,999,999,999.9900' })
              .optional(),
            useProduct: z.boolean().optional(),
            defaultValue: z.boolean().optional(),
            productName: z.string().optional(),
            physicalPropertiesId: z.number().nullable().optional(),
            physicalProperties: productPhysicalPropertiesSchema.optional().nullable(),
            temperatureSettingId: z.number().nullable().optional(),
            temperatureSetting: z
              .object({
                id: z.number().optional(),
                temperatureRangeId: z.number().optional(),
                temperatureUnitId: positiveNumber.refine(
                  (val) => val !== undefined && val !== null,
                  {
                    message: 'Unit is required',
                  }
                ),
                setPoint: createNumericField(z.number()),
                upperThresholdWarning: createNumericField(z.number().optional()).optional(),
                upperThresholdCritical: createNumericField(z.number()),
                lowerThresholdWarning: createNumericField(z.number().optional()).optional(),
                lowerThresholdCritical: createNumericField(z.number()),
                validFrom: z.union([z.date(), z.string()]).nullable().optional(),
                validTo: z.union([z.date(), z.string()]).nullable().optional(),
              })
              .optional()
              .nullable(),
            temperatureControlled: z.boolean().optional(),
            hsHeadingId: z.number().nullable().optional(),
            hsHeading: z
              .object({
                id: z.number().optional(),
                code: z.string().optional(),
                description: z.string().optional(),
              })
              .optional(),
            hsSubheadingId: z.number().nullable().optional(),
            hsSubheading: z
              .object({
                id: z.number().optional(),
                code: z.string().optional(),
                description: z.string().optional(),
              })
              .optional(),
            name: z.string().optional(),
            description: z.string().max(200, { message: 'Max 200 characters' }).optional(),
            validFrom: z.union([z.date(), z.string()]).nullable().optional(),
            validTo: z.union([z.date(), z.string()]).nullable().optional(),
            batchDetails: z
              .array(
                z.object({
                  id: z.number().optional(),
                  productId: z.number().optional(),
                  batchNumber: z.string().optional(),
                  expiryDate: z.union([z.date(), z.string()]).optional(),
                  validFrom: z.union([z.date(), z.string()]).nullable().optional(),
                  validTo: z.union([z.date(), z.string()]).nullable().optional(),
                })
              )
              .optional(),
            value: z
              .number()
              .max(Number.MAX_SAFE_INTEGER, { message: 'Max 9,999,999,999,999.9900' })
              .optional(),
          })
          .superRefine((data, ctx) => {
            if (data.useProduct) {
              if (!data.productId) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Product is required when using product',
                  path: ['productId'],
                })
              }
            } else if (!data.productDescription || data.productDescription.trim() === '') {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Product description is required when not using product',
                path: ['productDescription'],
              })
            }
          })
      )
      .optional(),
    isClearingRequired: z.boolean().optional(),
    isCrossBorder: z.boolean().optional(),
  })
  .superRefine((pack, ctx) => {
    const physical = pack.physicalProperties
    const gross = physical?.grossWeight ? physical.grossWeight : 0
    const net = physical?.netWeight ? physical.netWeight : 0
    const tare = physical?.tareWeight ? physical.tareWeight : 0
    const packagingTemplateId = pack.packagingTemplateId
    if (
      gross !== undefined &&
      gross !== null &&
      net !== undefined &&
      net !== null &&
      tare !== undefined &&
      !Number.isNaN(gross) &&
      !Number.isNaN(net) &&
      !Number.isNaN(tare)
    ) {
      const diff = Math.abs(gross - net - (tare ?? 0))

      if (diff > 0.01 && packagingTemplateId && packagingTemplateId === -1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Tare Weight must equal Gross Weight minus Net Weight',
          path: ['physicalProperties', 'tareWeight'],
        })
      }

      if (packagingTemplateId && packagingTemplateId > 0) {
        const weightDiff = net + (tare ?? 0) <= gross

        if (!weightDiff) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Payload Weight exceeds maximum allowed.',
            path: ['physicalProperties', 'netWeight'],
          })
        }
      }
    }
    if (pack.temperatureControlled) {
      if (pack.temperatureRange === undefined || pack.temperatureRange === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Temperature Range is required',
          path: ['temperatureRange'],
        })
      }
      if (pack.setPoint === undefined || pack.setPoint === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Set Point is required',
          path: ['setPoint'],
        })
      }
      if (!pack.setPointUnitId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Set Point Unit is required',
          path: ['setPoint'],
        })
      }
      if (pack.lowerCritical === undefined || pack.lowerCritical === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Lower Critical is required',
          path: ['lowerCritical'],
        })
      }
      if (pack.upperCritical === undefined || pack.upperCritical === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Upper Critical is required',
          path: ['upperCritical'],
        })
      }
      if (
        pack.setPoint !== undefined &&
        pack.upperCritical !== undefined &&
        pack.setPoint > pack.upperCritical
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Set Point cannot be more than Upper Critical',
          path: ['setPoint'],
        })
      }
      if (
        pack.upperCritical !== undefined &&
        pack.setPoint !== undefined &&
        pack.upperCritical < pack.setPoint
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Upper Critical cannot be lower than Set Point',
          path: ['upperCritical'],
        })
      }
      if (
        pack.lowerCritical !== undefined &&
        pack.setPoint !== undefined &&
        pack.lowerCritical > pack.setPoint
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Lower Critical cannot be more than Set Point',
          path: ['lowerCritical'],
        })
      }
      if (
        pack.lowerWarning !== undefined &&
        pack.lowerWarning !== null &&
        pack.lowerCritical !== undefined &&
        pack.lowerCritical !== null &&
        pack.lowerWarning < pack.lowerCritical
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Lower Warning cannot be less than Lower Critical',
          path: ['lowerWarning'],
        })
      }
      if (
        pack.upperWarning !== undefined &&
        pack.setPoint !== undefined &&
        pack.upperWarning < pack.setPoint
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Upper Warning cannot be lower than Set Point',
          path: ['upperWarning'],
        })
      }
    }
    if (pack.products && Array.isArray(pack.products)) {
      pack.products.forEach((product, idx) => {
        if (product.temperatureControlled) {
          if (
            product.temperatureSetting?.temperatureRangeId === undefined ||
            product.temperatureSetting?.temperatureRangeId === null
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Temperature Range is required',
              path: ['products', idx, 'temperatureSetting', 'temperatureRangeId'],
            })
          }

          if (
            product.temperatureSetting?.setPoint === undefined ||
            product.temperatureSetting?.setPoint === null
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Set Point is required',
              path: ['products', idx, 'temperatureSetting', 'setPoint'],
            })
          }
          if (!product.temperatureSetting?.temperatureUnitId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Set Point Unit is required',
              path: ['products', idx, 'temperatureSetting', 'temperatureUnitId'],
            })
          }
          if (
            product.temperatureSetting?.lowerThresholdCritical === undefined ||
            product.temperatureSetting?.lowerThresholdCritical === null
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Lower Critical is required',
              path: ['products', idx, 'temperatureSetting', 'lowerThresholdCritical'],
            })
          }
          if (
            product.temperatureSetting?.upperThresholdCritical === undefined ||
            product.temperatureSetting?.upperThresholdCritical === null
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Upper Critical is required',
              path: ['products', idx, 'temperatureSetting', 'upperThresholdCritical'],
            })
          }
          if (
            product.temperatureSetting?.setPoint !== undefined &&
            product.temperatureSetting?.lowerThresholdCritical !== undefined &&
            product.temperatureSetting.setPoint < product.temperatureSetting.lowerThresholdCritical
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Set Point cannot be less than Lower Critical',
              path: ['products', idx, 'temperatureSetting', 'setPoint'],
            })
          }
          if (
            product.temperatureSetting?.setPoint !== undefined &&
            product.temperatureSetting?.upperThresholdCritical !== undefined &&
            product.temperatureSetting.setPoint > product.temperatureSetting.upperThresholdCritical
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Set Point cannot be more than Upper Critical',
              path: ['products', idx, 'temperatureSetting', 'setPoint'],
            })
          }
          if (
            product.temperatureSetting?.upperThresholdCritical !== undefined &&
            product.temperatureSetting?.setPoint !== undefined &&
            product.temperatureSetting.upperThresholdCritical < product.temperatureSetting.setPoint
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Upper Critical cannot be lower than Set Point',
              path: ['products', idx, 'temperatureSetting', 'upperThresholdCritical'],
            })
          }
          if (
            product.temperatureSetting?.lowerThresholdCritical !== undefined &&
            product.temperatureSetting?.setPoint !== undefined &&
            product.temperatureSetting.lowerThresholdCritical > product.temperatureSetting.setPoint
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Lower Critical cannot be more than Set Point',
              path: ['products', idx, 'temperatureSetting', 'lowerThresholdCritical'],
            })
          }
          if (
            product.temperatureSetting?.lowerThresholdWarning !== undefined &&
            product.temperatureSetting?.lowerThresholdWarning !== null &&
            product.temperatureSetting?.lowerThresholdCritical !== undefined &&
            product.temperatureSetting?.lowerThresholdCritical !== null &&
            product.temperatureSetting.lowerThresholdWarning <
              product.temperatureSetting.lowerThresholdCritical
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Lower Warning cannot be less than Lower Critical',
              path: ['products', idx, 'temperatureSetting', 'lowerThresholdWarning'],
            })
          }
          if (
            product.temperatureSetting?.upperThresholdWarning !== undefined &&
            product.temperatureSetting?.upperThresholdWarning !== null &&
            product.temperatureSetting?.upperThresholdCritical !== undefined &&
            product.temperatureSetting?.upperThresholdCritical !== null &&
            product.temperatureSetting.upperThresholdWarning >
              product.temperatureSetting.upperThresholdCritical
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Upper Warning cannot be more than Upper Critical',
              path: ['products', idx, 'temperatureSetting', 'upperThresholdWarning'],
            })
          }
        }
        if (product.temperatureSetting) {
          if (
            product.temperatureSetting.temperatureRangeId === undefined ||
            product.temperatureSetting.temperatureRangeId === null ||
            product.temperatureSetting.temperatureRangeId < 1
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Temperature Range is required',
              path: ['products', idx, 'temperatureSetting', 'temperatureRangeId'],
            })
          }
          if (
            product.temperatureSetting.temperatureUnitId === undefined ||
            product.temperatureSetting.temperatureUnitId === null ||
            product.temperatureSetting.temperatureUnitId < 1
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Temperature Unit is required',
              path: ['products', idx, 'temperatureSetting', 'setPoint'],
            })
          }
          if (
            pack.temperatureRange !== undefined &&
            product.temperatureSetting.temperatureRangeId !== undefined &&
            pack.temperatureRange !== product.temperatureSetting.temperatureRangeId
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Product temperature range does not match Load configuration',
              path: ['products', idx, 'temperatureSetting', 'temperatureRangeId'],
            })
          }
        }
      })
    }
    if (!pack.products || pack.products.length === 0) {
      if (!pack.declaredContentDescription || pack.declaredContentDescription.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Declared Content Description is required',
          path: ['declaredContentDescription'],
        })
      }
      if (pack.declaredValue === null || pack.declaredValue === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Declared Value is required',
          path: ['declaredValue'],
        })
      }
    }

    if (pack.isClearingRequired && pack.products && Array.isArray(pack.products)) {
      pack.products.forEach((product, prodIndex) => {
        if (product.useProduct) {
          if (!product.hsHeadingId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'HS Code Heading is required when clearing is required',
              path: ['products', prodIndex, 'hsHeadingId'],
            })
          }
          if (!product.hsSubheadingId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'HS Code Subheading is required when clearing is required',
              path: ['products', prodIndex, 'hsSubheadingId'],
            })
          }
        }
      })
    }
    if (pack.isCrossBorder && pack.products && Array.isArray(pack.products)) {
      pack.products.forEach((product, prodIndex) => {
        if (!product.countryOfOriginId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Country of origin is required',
            path: ['products', prodIndex, 'countryOfOriginId'],
          })
        }
      })
    }
  })

export type IPackagingItem = z.infer<typeof packagingItemSchema>
